package se.mittpass.web.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.AbsenceRequestDto
import se.mittpass.shared.api.SlotDto
import se.mittpass.shared.api.StaffMemberDto
import se.mittpass.web.*

@Composable
fun MobileAgendaView(
    startDate: String,
    endDate: String,
    staff: List<StaffMemberDto>,
    slots: List<SlotDto>,
    absences: List<AbsenceRequestDto>,
    isManager: Boolean,
    currentUserId: Long?,
    onSlotClick: (SlotDto) -> Unit,
    onAbsenceClick: (AbsenceRequestDto) -> Unit,
    onCreateSlot: (day: String, startMin: Int, endMin: Int, staffId: Long?) -> Unit,
    onClaimSlot: (Long) -> Unit,
    onUnclaimSlot: (Long) -> Unit
) {
    val today = todayLocalDate()
    val daysList = remember(startDate, endDate) {
        val list = mutableListOf<String>()
        var curr = startDate
        while (curr <= endDate) {
            list.add(curr)
            curr = addDays(curr, 1)
        }
        list
    }

    var selectedDay by remember(startDate, endDate) {
        mutableStateOf(if (today in daysList) today else daysList.firstOrNull() ?: today)
    }

    val dayNames = listOf("Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön")

    // Filter data for the selected day
    val daySlots = remember(slots, selectedDay) {
        slots.filter { it.day == selectedDay }.sortedBy { it.startTime }
    }
    val unassignedSlots = remember(daySlots) {
        daySlots.filter { it.assignedToUserId == null }
    }
    val assignedSlots = remember(daySlots) {
        daySlots.filter { it.assignedToUserId != null }
    }
    val dayAbsences = remember(absences, selectedDay) {
        absences.filter { it.startDate <= selectedDay && it.endDate >= selectedDay }
    }

    Div({
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "1rem")
            property("width", "100%")
        }
    }) {
        // ── 1. Horizontal Date Strip (Week/Day Selector) ──
        Div({
            style {
                property("display", "flex")
                property("gap", "0.4rem")
                property("overflow-x", "auto")
                property("padding", "0.25rem 0.1rem 0.6rem")
                property("scrollbar-width", "none")
                property("justify-content", if (daysList.size <= 7) "space-between" else "flex-start")
            }
        }) {
            daysList.forEach { dayStr ->
                val isSelected = dayStr == selectedDay
                val isToday = dayStr == today
                val dateObj = kotlin.js.Date(dayStr)
                val jsDay = dateObj.getDay()
                val dayOfWeekIdx = if (jsDay == 0) 6 else jsDay - 1
                val dayName = dayNames.getOrElse(dayOfWeekIdx) { "" }
                val dayNum = dayStr.takeLast(2)
                val isWeekend = dayOfWeekIdx >= 5

                val hasUnassigned = slots.any { it.day == dayStr && it.assignedToUserId == null }
                val slotCount = slots.count { it.day == dayStr }

                Button({
                    onClick { selectedDay = dayStr }
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("padding", "0.5rem 0.4rem")
                        property("min-width", "46px")
                        property("border-radius", "14px")
                        property("border", if (isSelected) "2px solid var(--rp-pine)" else if (isToday) "1.5px solid var(--rp-muted)" else "1px solid var(--rp-hl-med)")
                        property("background", if (isSelected) "var(--rp-pine)" else if (isToday) "var(--rp-info-bg)" else "var(--rp-surface)")
                        property("color", if (isSelected) "#FFFFFF" else if (isWeekend) "var(--rp-err-text)" else "var(--rp-text)")
                        property("cursor", "pointer")
                        property("transition", "all 0.15s ease")
                        if (isSelected) property("box-shadow", "0 4px 12px rgba(44,63,168,0.25)")
                    }
                }) {
                    Span({
                        style {
                            property("font-size", "0.7rem")
                            property("font-weight", "600")
                            property("opacity", if (isSelected) "0.9" else "0.7")
                        }
                    }) { Text(dayName) }
                    Span({
                        style {
                            property("font-size", "1.05rem")
                            property("font-weight", "800")
                            property("margin", "2px 0")
                        }
                    }) { Text(dayNum) }

                    // Dot indicator for activity
                    Div({
                        style {
                            property("width", "5px")
                            property("height", "5px")
                            property("border-radius", "50%")
                            property("background", if (hasUnassigned) "var(--rp-gold)" else if (slotCount > 0) (if (isSelected) "var(--rp-surface)" else "var(--rp-pine)") else "transparent")
                        }
                    }) {}
                }
            }
        }

        // ── 2. Day Summary Header & Quick Add ──
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("background", "var(--rp-surface)")
                property("border", "1px solid var(--rp-hl-med)")
                property("border-radius", "16px")
                property("padding", "0.75rem 1rem")
            }
        }) {
            Div {
                H4({
                    style {
                        property("margin", "0")
                        property("font-size", "1rem")
                        property("font-weight", "800")
                        property("color", "var(--rp-text)")
                    }
                }) {
                    Text(formatNiceDate(selectedDay))
                }
                Span({
                    style {
                        property("font-size", "0.75rem")
                        property("color", "var(--rp-subtle)")
                        property("font-weight", "600")
                    }
                }) {
                    val passWord = if (daySlots.size == 1) "pass" else "pass"
                    Text("${daySlots.size} $passWord schemalagt" + if (unassignedSlots.isNotEmpty()) " · ${unassignedSlots.size} otilldelat" else "")
                }
            }

            if (isManager) {
                Button({
                    onClick { onCreateSlot(selectedDay, 8 * 60, 16 * 60, null) }
                    style {
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                        property("color", "#FFFFFF")
                        property("border", "none")
                        property("border-radius", "50px")
                        property("padding", "0.45rem 0.9rem")
                        property("font-size", "0.8rem")
                        property("font-weight", "700")
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.4rem")
                        property("cursor", "pointer")
                        property("box-shadow", "0 2px 8px rgba(108,60,225,0.25)")
                    }
                }) {
                    FaIcon("fa-solid fa-plus", size = "0.75rem", color = "var(--rp-surface)")
                    Text("Nytt pass")
                }
            }
        }

        // ── 3. Unassigned Shifts Card (if any) ──
        if (unassignedSlots.isNotEmpty()) {
            Div({
                style {
                    property("background", "var(--rp-warn-bg)")
                    property("border", "1px solid var(--rp-warn-border)")
                    property("border-radius", "16px")
                    property("padding", "1rem")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "0.6rem")
                }
            }) {
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                    FaIcon("fa-solid fa-triangle-exclamation", size = "0.9rem", color = "var(--rp-warn-text)")
                    Span({ style { property("font-size", "0.85rem"); property("font-weight", "800"); property("color", "var(--rp-warn-text)") } }) {
                        Text("Otilldelade pass som behöver bemannas (${unassignedSlots.size})")
                    }
                }

                unassignedSlots.forEach { slot ->
                    val hasClaimed = currentUserId != null && slot.interestedUsers.any { it.id == currentUserId }
                    Div({
                        onClick { onSlotClick(slot) }
                        style {
                            property("background", "var(--rp-surface)")
                            property("border", "1px solid var(--rp-warn-border)")
                            property("border-radius", "12px")
                            property("padding", "0.75rem 0.9rem")
                            property("display", "flex")
                            property("justify-content", "space-between")
                            property("align-items", "center")
                            property("cursor", "pointer")
                        }
                    }) {
                        Div {
                            Span({ style { property("font-size", "0.95rem"); property("font-weight", "800"); property("color", "var(--rp-text)"); property("display", "block") } }) {
                                Text("${slot.startTime.take(5)} – ${slot.endTime.take(5)}")
                            }
                            if (slot.interestedUsers.isNotEmpty()) {
                                Span({ style { property("font-size", "0.75rem"); property("color", "var(--rp-pine)"); property("font-weight", "700"); property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.3rem") } }) {
                                    FaIcon("fa-solid fa-hand", size = "0.7rem", color = "var(--rp-pine)")
                                    Text("${slot.interestedUsers.size} söker passet")
                                }
                            } else {
                                Span({ style { property("font-size", "0.75rem"); property("color", "var(--rp-muted)") } }) {
                                    Text("Ingen sökande ännu")
                                }
                            }
                        }

                        if (!isManager && currentUserId != null) {
                            Button({
                                onClick {
                                    if (hasClaimed) onUnclaimSlot(slot.id) else onClaimSlot(slot.id)
                                }
                                style {
                                    property("background", if (hasClaimed) "var(--rp-overlay)" else "var(--rp-pine)")
                                    property("color", if (hasClaimed) "var(--rp-text)" else "#FFFFFF")
                                    property("border", "none")
                                    property("border-radius", "50px")
                                    property("padding", "0.35rem 0.8rem")
                                    property("font-size", "0.78rem")
                                    property("font-weight", "700")
                                    property("cursor", "pointer")
                                }
                            }) {
                                Text(if (hasClaimed) "Avanmäl" else "Ta pass")
                            }
                        } else {
                            FaIcon("fa-solid fa-chevron-right", size = "0.8rem", color = "var(--rp-warn-text)")
                        }
                    }
                }
            }
        }

        // ── 4. Assigned Staff Shift List ──
        if (assignedSlots.isEmpty() && unassignedSlots.isEmpty() && dayAbsences.isEmpty()) {
            Div({
                style {
                    property("background", "var(--rp-surface)")
                    property("border", "1px dashed var(--rp-hl-med)")
                    property("border-radius", "16px")
                    property("padding", "3rem 1.5rem")
                    property("text-align", "center")
                    property("color", "var(--rp-muted)")
                }
            }) {
                FaIcon("fa-solid fa-calendar-day", size = "2rem", color = "var(--rp-muted)")
                Span({
                    style {
                        property("display", "block")
                        property("font-weight", "700")
                        property("color", "var(--rp-text)")
                        property("margin", "0.75rem 0 0.25rem")
                        property("font-size", "0.95rem")
                    }
                }) { Text("Inga pass schemalagda") }
                Span({ style { property("font-size", "0.82rem") } }) { Text("Ingen personal är inbokad denna dag.") }
            }
        } else {
            Div({
                style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "0.5rem")
                }
            }) {
                // Scheduled Staff Cards
                assignedSlots.forEach { slot ->
                    val member = staff.find { it.userId == slot.assignedToUserId }
                    val memberName = member?.let { "${it.firstName ?: ""} ${it.lastName ?: ""}".trim().ifEmpty { it.email } } ?: "Tilldelad"
                    val memberRole = member?.role?.replaceFirstChar { it.uppercase() } ?: "Personal"
                    val initials = memberName.split(" ").mapNotNull { it.take(1).ifEmpty { null } }.joinToString("").take(2).uppercase().ifEmpty { "P" }
                    val memberColorScheme = getEmployeeColorScheme(member?.color, slot.assignedToUserId)

                    Div({
                        onClick { onSlotClick(slot) }
                        style {
                            property("background", "var(--rp-surface)")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("border-left", "4px solid ${memberColorScheme.primary}")
                            property("border-radius", "14px")
                            property("padding", "0.85rem 1rem")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "space-between")
                            property("cursor", "pointer")
                            property("box-shadow", "0 1px 3px rgba(13,13,24,0.03)")
                        }
                    }) {
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem") } }) {
                            // Avatar Pill
                            Div({
                                style {
                                    property("width", "36px")
                                    property("height", "36px")
                                    property("border-radius", "50%")
                                    property("background", memberColorScheme.primary)
                                    property("color", "#FFFFFF")
                                    property("font-weight", "800")
                                    property("font-size", "0.8rem")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                }
                            }) { Text(initials) }

                            Div {
                                Span({
                                    style {
                                        property("font-size", "0.92rem")
                                        property("font-weight", "700")
                                        property("color", "var(--rp-text)")
                                        property("display", "block")
                                    }
                                }) { Text(memberName) }
                                Span({
                                    style {
                                        property("font-size", "0.75rem")
                                        property("color", "var(--rp-subtle)")
                                        property("font-weight", "500")
                                    }
                                }) { Text(memberRole) }
                            }
                        }

                        Div({
                            style {
                                property("background", memberColorScheme.bg)
                                property("color", memberColorScheme.text)
                                property("border", "1px solid ${memberColorScheme.border}")
                                property("border-radius", "50px")
                                property("padding", "0.35rem 0.75rem")
                                property("font-size", "0.85rem")
                                property("font-weight", "800")
                            }
                        }) {
                            Text("${slot.startTime.take(5)} – ${slot.endTime.take(5)}")
                        }
                    }
                }

                // Absences on selected day
                dayAbsences.forEach { abs ->
                    val member = staff.find { it.userId == abs.userId }
                    val memberName = member?.let { "${it.firstName ?: ""} ${it.lastName ?: ""}".trim().ifEmpty { it.email } } ?: "Personal"
                    val isVacation = abs.type == "vacation"

                    Div({
                        onClick { onAbsenceClick(abs) }
                        style {
                            property("background", if (isVacation) "var(--rp-ok-bg)" else "var(--rp-err-bg)")
                            property("border", "1px solid " + if (isVacation) "var(--rp-ok-border)" else "var(--rp-err-border)")
                            property("border-radius", "14px")
                            property("padding", "0.75rem 1rem")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "space-between")
                            property("cursor", "pointer")
                        }
                    }) {
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.6rem") } }) {
                            FaIcon(if (isVacation) "fa-solid fa-umbrella-beach" else "fa-solid fa-calendar-xmark", size = "0.9rem", color = if (isVacation) "var(--rp-ok-text)" else "var(--rp-err-text)")
                            Div {
                                Span({ style { property("font-size", "0.85rem"); property("font-weight", "700"); property("color", if (isVacation) "var(--rp-ok-text)" else "var(--rp-err-text)"); property("display", "block") } }) {
                                    Text(memberName)
                                }
                                Span({ style { property("font-size", "0.72rem") } }) {
                                    Text(if (isVacation) "Semester" else "Frånvaro / Sjuk")
                                }
                            }
                        }
                        Span({
                            style {
                                property("font-size", "0.75rem")
                                property("font-weight", "700")
                                property("background", "var(--rp-surface)")
                                property("padding", "0.2rem 0.55rem")
                                property("border-radius", "50px")
                                property("color", if (isVacation) "var(--rp-ok-text)" else "var(--rp-err-text)")
                            }
                        }) {
                            Text(abs.status.replaceFirstChar { it.uppercase() })
                        }
                    }
                }
            }
        }
    }
}
